/**
 * BuddyBee chip definitions — split by auth state + current page.
 *
 * Two flavors of chips:
 *   1) `launch`  — power-launcher: navigates to a feature page on click.
 *   2) `text`    — pre-filled question: sends the text to /api/ai/chat.
 *
 * Logged-OUT chips never reference protected features (Wallet, Library, Jobs).
 */

// ─── LOGGED-OUT base chips (public knowledge questions only) ─────────────────
export const GUEST_CHIPS = [
  { id: "g-what",       label: "What is TeeBee AI?",            text: "What is TeeBee AI?" },
  { id: "g-coins",      label: "How do TeeBee Coins work?",      text: "How do TeeBee Coins work?" },
  { id: "g-explore",    label: "Explore lessons",                launch: "/explore" },
  { id: "g-market",     label: "Hire educators",                 launch: "/marketplace" },
  { id: "g-pricing",    label: "View pricing",                   launch: "/#pricing" },
  { id: "g-growth",     label: "What is Growth Studio?",         text: "What is Growth Studio?" },
  { id: "g-signup",     label: "Create free account",            launch: "/signup" },
  { id: "g-signin",     label: "Sign in",                        launch: "/login" },
];

// ─── LOGGED-IN universal chips (always available regardless of page) ─────────
export const AUTH_CHIPS = [
  { id: "a-kit",        label: "✨ Full Teaching Kit",            launch: "/teach/kit" },
  { id: "a-diff",       label: "🎯 Differentiate a lesson",       launch: "/teach/kit?tab=differentiate" },
  { id: "a-tfa",        label: "🪄 Teach from anything",          launch: "/teach-from-anything" },
  { id: "a-ocr",        label: "📷 Scan with OCR",                launch: "/ocr" },
  { id: "a-resume",     label: "📄 Build my CV",                  launch: "/resume" },
  { id: "a-growth",     label: "🌱 Improve my profile",           launch: "/growth-studio" },
  { id: "a-jobs",       label: "💼 Find jobs",                    launch: "/jobs" },
  { id: "a-wallet",     label: "🪙 Open Wallet",                  launch: "/wallet" },
  { id: "a-coins-help", label: "Why were coins deducted?",        text: "Why were coins deducted from my wallet?" },
  { id: "a-support",    label: "Contact support",                 text: "How do I contact human support?" },
];

// ─── Page-contextual chips: shown above the universal chips when route matches.
// Order matters — first matching prefix wins.
export const CONTEXT_CHIPS = [
  {
    prefix: "/wallet",
    chips: [
      { id: "ctx-coin-explain", label: "Explain TeeBee Coins",       text: "Walk me through TeeBee Coins — earning, spending, refunds." },
      { id: "ctx-recharge",     label: "How to recharge?",            text: "How do I recharge my TeeBee Coins?" },
      { id: "ctx-txn",          label: "Read my transactions",        text: "Help me understand my last few transactions." },
    ],
  },
  {
    prefix: "/profile",
    chips: [
      { id: "ctx-profile-fix", label: "Improve my profile",         launch: "/growth-studio" },
      { id: "ctx-visibility",  label: "Profile visibility settings", text: "How do profile visibility settings work?" },
      { id: "ctx-publish",     label: "Publish to marketplace",      text: "How do I publish my profile to the marketplace?" },
    ],
  },
  {
    prefix: "/lesson",  // matches /lessons + /lesson/:id
    chips: [
      { id: "ctx-diff",      label: "🎯 Differentiate this lesson", launch: "/teach/kit?tab=differentiate" },
      { id: "ctx-export",    label: "Export as PDF",                text: "How do I export my lesson as a PDF?" },
      { id: "ctx-share",     label: "Publish & share",              text: "How do I publish a lesson so other teachers can find it?" },
    ],
  },
  {
    prefix: "/teach/kit",
    chips: [
      { id: "ctx-kit-explain", label: "What's in a Full Teaching Kit?", text: "What are the 14 outputs in a Full Teaching Kit?" },
      { id: "ctx-kit-fail",    label: "My kit didn't generate",         text: "My Full Teaching Kit didn't generate properly. What do I do?" },
    ],
  },
  {
    prefix: "/jobs",
    chips: [
      { id: "ctx-jobs-apply",  label: "How do I apply?",             text: "How do I apply for a job on TeeBee?" },
      { id: "ctx-jobs-cv",     label: "Improve my CV first",          launch: "/resume" },
    ],
  },
  {
    prefix: "/marketplace",
    chips: [
      { id: "ctx-mp-explain",  label: "How does Marketplace work?",   text: "How does the educator marketplace work?" },
      { id: "ctx-mp-pub",      label: "Publish my profile",           text: "How do I publish my profile to the marketplace?" },
    ],
  },
  {
    prefix: "/resume",
    chips: [
      { id: "ctx-resume-tips", label: "CV writing tips",              text: "What makes a great teacher CV in 2026?" },
      { id: "ctx-resume-fix",  label: "Auto-improve my CV",           launch: "/growth-studio/cv-improve" },
    ],
  },
  {
    prefix: "/inbox",
    chips: [
      { id: "ctx-connect", label: "What is Secure Connect?",       text: "Tell me about Secure Connect." },
      { id: "ctx-pay",     label: "Why does it cost coins?",        text: "Why does Secure Connect cost TeeBee Coins?" },
    ],
  },
];

/** Pick which chip-set to show given pathname + auth state. */
export function pickChips({ authed, pathname }) {
  if (!authed) return GUEST_CHIPS;
  const ctx = CONTEXT_CHIPS.find((c) => pathname.startsWith(c.prefix));
  if (ctx) return [...ctx.chips, ...AUTH_CHIPS.slice(0, 6)];
  return AUTH_CHIPS;
}
